import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarRange,
  Gauge as GaugeIcon,
  IndianRupee,
  ListChecks,
  Loader2,
  PenLine,
} from "lucide-react";
import { toast } from "sonner";
import { MultiSelect } from "@/components/MultiSelect";
import { CompareBars, Donut, SmartTile } from "@/components/SmartTile";
import { OdometerBoard } from "@/components/OdometerBoard";
import { ComplianceHealth } from "@/components/ComplianceHealth";
import { UnitLayer } from "@/components/UnitLayer";
import { SignatureQueue } from "@/components/SignatureQueue";
import { ChaseBoard, useChase } from "@/components/ChasePanel";
import { MissingDataPanel } from "@/components/MissingDataPanel";
import { Expander } from "@/components/Expander";
import { ExportButton } from "@/components/ExportButton";
import { ProjectDetailSheet } from "@/components/ProjectDetailSheet";
import { ProjectFormDialog } from "@/components/ProjectFormDialog";
import { StatusPill, StageTag } from "@/components/StatusPill";
import { api, apiError, fmtCr, fmtDay, fmtLakhValue, fmtPct, PROGRESS, STAGE_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLOUR = {
  Identified: "hsl(var(--watch))",
  Ongoing: "hsl(var(--primary))",
  "Put on Hold": "hsl(25 5% 45%)",
  Dropped: "hsl(0 0% 58%)",
  "Completed and Running": "hsl(var(--healthy))",
  "Completed and Discontinued": "hsl(25 5% 45%)",
};
const STATUS_LINK = {
  Identified: "/projects?status=Identified",
  Ongoing: "/projects?status=Ongoing",
  "Put on Hold": "/projects?status=Put%20on%20Hold",
  Dropped: "/projects?status=Dropped",
  "Completed and Running": "/projects?status=Completed",
  "Completed and Discontinued": "/projects?status=Completed",
};

export default function Dashboard() {
  const { user, isPartner, shows, canReadAll, scopedPlant } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [fys, setFys] = useState("all");
  const [plants, setPlants] = useState(scopedPlant && !canReadAll ? scopedPlant : "");
  const [tile, setTile] = useState("");
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [fix, setFix] = useState(null);
  const { chase } = useChase(plants.includes(",") ? "" : plants, "", false, !isPartner);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const load = () => {
    api
      .get("/dashboard", { params: { fys, plants } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
    api
      .get("/current-fy", { params: { fys, plants } })
      .then((r) => setRows(r.data.rows))
      .catch(() => setRows([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [fys, plants]);

  // The use case form is opened at the vetting entry from the signature queue.
  const openVetting = async (row) => {
    try {
      const { data: detail } = await api.get(`/projects/${row.id}`);
      setFix({ project: detail.project, field: "actual_benefit_cr" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const u = data.usecases;
  const b = data.benefit;
  const c = data.compliance;
  const firstName = user?.name?.split(" ")[0];
  const flip = (key) => setTile((v) => (v === key ? "" : key));

  return (
    <div className="grid-noise min-h-screen" data-testid="dashboard-page">
      <div className="p-5 sm:p-8">
        {/* ── the year, and who is looking ─────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div
              className="font-display text-[11px] font-black uppercase tracking-[0.26em] text-primary"
              data-testid="dashboard-scope-title"
            >
              {data.scope.plant
                ? `${data.scope.plant} · unit view`
                : data.scope.chosen && data.scope.chosen.length < data.scope.units.length
                  ? `${data.scope.chosen.join(" · ")} · picked units`
                  : data.scope.own_unit_only
                    ? `${data.scope.units.join(" · ")} · your units`
                    : "Pravartanam · all units"}
            </div>
            <h1
              className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl"
              data-testid="dashboard-greeting"
            >
              {firstName ? `नमस्कार, ${firstName}` : "नमस्कार"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Every digitalisation usecase of{" "}
              <b className="font-data text-foreground">{data.fy.label}</b> — what stands where,
              what it is worth, and how the live ones are actually performing. Press any figure to
              see the working behind it.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-px bg-border">
            <div className="bg-card px-4 py-3">
              <span className="label-caps flex items-center gap-1.5">
                <CalendarRange className="h-3 w-3" /> Financial year
              </span>
              <div className="mt-1.5">
                <MultiSelect
                  label={data.fy.label}
                  testId="fy-slicer"
                  width="w-[210px]"
                  value={fys}
                  onChange={setFys}
                  options={[
                    { value: "all", label: "All years", hint: "every year on the register" },
                    ...data.fy.options.map((label) => ({ value: label, label })),
                  ]}
                />
              </div>
              <p className="mt-1.5 max-w-[210px] text-[10px] leading-snug text-muted-foreground">
                {data.fy.all
                  ? "Every year the register has work in."
                  : data.fy.is_current
                    ? "The year that is running — 1 April to 31 March."
                    : `${data.fy.from} to ${data.fy.to}. Every figure on this page is of that.`}{" "}
                Pick as many years as you need.
              </p>
            </div>
            <div className="bg-card px-4 py-3">
              <span className="label-caps">Unit</span>
              <div className="mt-1.5">
                <MultiSelect
                  label={
                    plants
                      ? `${plants.split(",").length} unit(s)`
                      : canReadAll
                        ? "All units I may see"
                        : data.scope.units.join(" · ")
                  }
                  testId="dashboard-plant"
                  width="w-[210px]"
                  value={plants}
                  onChange={setPlants}
                  options={(meta?.plants || [])
                    .filter((p) => data.scope.units.includes(p.code))
                    .map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` }))}
                />
              </div>
              <p className="mt-1.5 max-w-[210px] text-[10px] leading-snug text-muted-foreground">
                {u.total} of the register's {u.of_register} usecases belong to this view.
              </p>
            </div>
          </div>
        </div>

        {/* ── the headline tiles ───────────────────────────────────────────────────────── */}
        {shows("dashboard.tiles") && (
          <div
            className="mt-8 grid gap-px bg-border md:grid-cols-2 xl:grid-cols-3"
            data-testid="dashboard-tiles"
          >
            <SmartTile
              testId="tile-usecases"
              label={data.fy.all ? "Usecases on the register" : "Usecases this year"}
              value={u.total}
              icon={ListChecks}
              accent="hsl(var(--primary))"
              sub={`${u.delayed} running late, ${Math.max(0, u.needs_push - u.delayed)} with something to fix`}
              open={tile === "usecases"}
              onToggle={() => flip("usecases")}
            >
              <Donut
                testId="donut-status"
                total={u.total}
                caption="usecases"
                slices={u.order
                  .map((key) => ({
                    key: key.replace(/[^A-Za-z]+/g, "-"),
                    label: u.by_status[key].label,
                    value: u.by_status[key].count,
                    colour: STATUS_COLOUR[key] || STAGE_COLORS.Ongoing,
                    onClick: () => navigate(STATUS_LINK[key] || "/projects"),
                  }))
                  .filter((s) => s.value > 0)}
              />
              <p className="mt-4 border-t border-border pt-3 text-[11px] leading-relaxed text-muted-foreground">
                {data.fy.all ? (
                  <>
                    Every year means the whole register — all {u.of_register} usecase(s) in this
                    view, Long-Term work and rows with no dates included.{" "}
                    {u.no_year
                      ? `${u.no_year} of them belong to no financial year yet, because they are Long-Term or carry no date the portal can place.`
                      : "Every one of them can be placed in a financial year."}
                  </>
                ) : (
                  <>
                    A usecase belongs to {data.fy.label} because it was completed inside it, is
                    committed to finish inside it, or has been worked on during it. Long-Term work
                    never does — pick <b>All years</b> to see the whole register.{" "}
                    {u.term_awaited
                      ? `${u.term_awaited} usecase(s) still carry no declared Term.`
                      : "Every usecase in play carries a declared Term."}
                  </>
                )}
              </p>
            </SmartTile>

            <SmartTile
              testId="tile-benefit"
              label="Expected annual benefit"
              value={b.expected_cr.toFixed(0)}
              unit="₹ Cr"
              icon={IndianRupee}
              accent="hsl(var(--healthy))"
              sub={`${b.expected_pct}% of SAIL's ₹${data.target_cr} Cr mission`}
              open={tile === "benefit"}
              onToggle={() => flip("benefit")}
            >
              <CompareBars
                testId="bars-benefit"
                bars={[
                  {
                    key: "expected",
                    label: "Expected — as the units have committed",
                    value: b.expected_cr,
                    readout: fmtCr(b.expected_cr),
                    colour: "hsl(var(--primary))",
                    note: `across ${u.total} usecase(s) of ${data.fy.label}`,
                  },
                  {
                    key: "live",
                    label: "Already live — at Go-Live or beyond",
                    value: b.live_cr,
                    readout: fmtCr(b.live_cr),
                    colour: "hsl(199 89% 48%)",
                  },
                  {
                    key: "vetted",
                    label: "Actually vetted — signed, with a date",
                    value: b.vetted_cr,
                    readout: b.vetted_cr ? fmtCr(b.vetted_cr) : "nothing yet",
                    colour: "hsl(var(--healthy))",
                    note: b.vetted_count
                      ? `vetted on ${b.vetted_count} usecase(s)`
                      : "no benefit has been signed off this year",
                  },
                ]}
              />
              <p className="mt-4 border-t border-border pt-3 text-[11px] leading-relaxed text-muted-foreground">
                A benefit counts as vetted only with a figure <i>and</i> the day it was signed.{" "}
                {b.awaiting_count
                  ? `${b.awaiting_count} completed usecase(s) worth ${fmtCr(b.awaiting_cr)} could be signed today — see whose signature it is waiting on, below.`
                  : "Nothing completed is waiting on a signature."}
              </p>
              {!!b.leaders?.length && (
                <dl
                  className="mt-3 space-y-1.5 border-t border-border pt-3 text-[11px]"
                  data-testid="benefit-leaders"
                >
                  <dt className="label-caps">Carrying the most of it</dt>
                  {b.leaders.map((l) => (
                    <dd key={l.code} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate text-muted-foreground">
                        {l.plant} · {l.code} — {l.title}
                      </span>
                      <span className="font-data shrink-0 font-bold tabular-nums">
                        {fmtCr(l.expected_cr)}
                      </span>
                    </dd>
                  ))}
                </dl>
              )}
            </SmartTile>

            <SmartTile
              testId="tile-compliance"
              label="Compliance under watch"
              value={c.avg_cumulative_pct === null ? "—" : c.avg_cumulative_pct.toFixed(0)}
              unit={c.avg_cumulative_pct === null ? "" : "%"}
              icon={GaugeIcon}
              accent="hsl(var(--watch))"
              sub={`cumulative across ${c.on_dashboard} usecase(s) · ${c.reading_days} day(s) on record`}
              open={tile === "compliance"}
              onToggle={() => flip("compliance")}
            >
              <CompareBars
                testId="bars-compliance"
                bars={[
                  {
                    key: "target",
                    label: "Target impact committed",
                    value: c.target_lakh || 0,
                    readout: fmtLakhValue(c.target_lakh),
                    colour: "hsl(var(--primary))",
                  },
                  {
                    key: "actual",
                    label: "Impact recorded so far",
                    value: c.actual_lakh || 0,
                    readout: fmtLakhValue(c.actual_lakh),
                    colour: "hsl(var(--healthy))",
                  },
                ]}
              />
              <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5 border-t border-border pt-3 text-[11px]">
                {[
                  ["Sending compliance data", c.tracked],
                  ["Showing on this page", c.on_dashboard],
                  ["Ready with all three targets", c.declared ?? 0],
                  ["Connected to a plant system", c.connected ?? 0],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="font-data font-bold tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                A day with no compliance per cent against it reads <b>N/A</b>, not nought — it is
                filled on the Compliance Data screen, or posted by the plant's own system.
              </p>
            </SmartTile>
          </div>
        )}

        {/* ── unit by unit, each opening into its own detail ───────────────────────────── */}
        {shows("dashboard.units") && (
          <div className="mt-10">
            <UnitLayer units={data.units} groups={data.groups} fy={data.fy.label} />
          </div>
        )}

        {/* ── the compliance odometers, usecase by usecase, in the officer's own order ──── */}
        {shows("dashboard.odometers") && (
          <div className="mt-10">
            <OdometerBoard plants={plants} fys={fys} />
          </div>
        )}

        {/* ── who is keeping their readings up — the quiet band at the foot ─────────────── */}
        {shows("dashboard.compliance_health") && (
          <div className="mt-10">
            <ComplianceHealth plants={plants} />
          </div>
        )}

        {/* ── what is waiting, kept out of the way until it is asked for ───────────────── */}
        <div className="mt-10 space-y-px bg-border" data-testid="dashboard-secondary-sections">
          {shows("dashboard.signatures") && (
            <Expander
              testId="dashboard-expander-signatures"
              title="Whose signature the benefit is waiting on"
              note={
                b.awaiting_count
                  ? `${b.awaiting_count} completed usecase(s) worth ${fmtCr(b.awaiting_cr)} can be signed today.`
                  : "Grouped by the officer who must vet each figure."
              }
            >
              <SignatureQueue
                query={{ plants, fys }}
                onFix={openVetting}
                testId="signature-queue"
              />
            </Expander>
          )}

          {shows("dashboard.fy_table") && (
            <Expander
              testId="dashboard-expander-fy-table"
              title={`The ${data.fy.label} register`}
              note={`${u.total} usecase(s), ${fmtCr(b.expected_cr)} expected. Press a row to open it.`}
            >
              <div className="flex justify-end">
                <ExportButton
                  path="/current-fy/export"
                  params={{ plants, fys }}
                  label="Download this year"
                  testId="export-fy"
                />
              </div>
              <div className="mt-3 divide-y divide-border border border-border bg-card">
                {(rows || []).slice(0, 40).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`fy-row-${p.code}`}
                    onClick={() => setOpenId(p.id)}
                    className="w-full border-l-2 border-transparent px-4 py-3 text-left transition-colors duration-200 hover:border-primary hover:bg-muted"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{p.title}</div>
                        <div className="font-data text-[10px] text-muted-foreground">
                          {p.code} · {p.plant} · {p.phase_view || "sub-stage not set"} · updated{" "}
                          {fmtDay(p.last_updated_on)}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="font-data text-xs font-bold">
                          {fmtCr(p.impact?.expected_cr)}
                        </span>
                        <StageTag stage={p.status} />
                        <StatusPill progress={p.progress} compact testId={`fy-status-${p.code}`} />
                      </div>
                    </div>
                  </button>
                ))}
                {rows && !rows.length && (
                  <p className="p-6 text-sm text-muted-foreground">
                    No usecase belongs to {data.fy.label} in this view.
                  </p>
                )}
                {rows && rows.length > 40 && (
                  <button
                    type="button"
                    data-testid="fy-see-all"
                    onClick={() => navigate(`/projects?plant=${plants.split(",")[0] || ""}`)}
                    className="w-full bg-muted px-4 py-3 text-center font-display text-[10px] font-bold uppercase tracking-widest transition-opacity duration-200 hover:opacity-80"
                  >
                    See all {rows.length} on the register
                  </button>
                )}
              </div>
            </Expander>
          )}

          {shows("dashboard.chase") && chase && (
            <Expander
              testId="dashboard-expander-chase"
              title="What needs updating"
              note="Five columns the units keep leaving open, and every one shows up as a hole somewhere else."
            >
              <ChaseBoard chase={chase} />
            </Expander>
          )}

          {shows("dashboard.missing") && (
            <Expander
              testId="dashboard-expander-missing"
              title="Missing Data Report"
              note="The mandatory entries the units have left blank, and the workbook of them."
            >
              <MissingDataPanel />
            </Expander>
          )}
        </div>
      </div>

      <ProjectDetailSheet
        projectId={openId}
        open={!!openId}
        onOpenChange={(o) => !o && setOpenId(null)}
        onClose={() => setOpenId(null)}
        meta={meta}
        onChanged={load}
      />

      <ProjectFormDialog
        open={!!fix}
        onOpenChange={(o) => !o && setFix(null)}
        project={fix?.project || null}
        focusField={fix?.field || ""}
        meta={meta}
        onSaved={() => {
          setFix(null);
          load();
        }}
      />
    </div>
  );
}
